<script setup lang="ts">
import { formatHttpError, getErrorStatus } from "~/utils/apiError";
import { SSO_DISABLED_COPY } from "~/utils/ssoCopy";

interface Props {
  redirect?: boolean | string;
}

const props = withDefaults(defineProps<Props>(), {
  redirect: true,
});

const config = useRuntimeConfig();
const authStore = useAuthStore();
const { track } = useTracking();

// Progressive disclosure: show a single "Sign in with SSO" button; on click,
// reveal a work-email input. The domain of that email decides which client's
// IdP the user is routed to (resolved server-side via /api/public/auth/lookup).
const expanded = ref(false);
const email = ref("");
const loading = ref(false);
const error = ref("");
// Null until `expanded` renders the v-else branch; continueSso only runs from
// there, but it is optional-chained anyway.
const emailInput = ref<{ flush: () => void } | null>(null);

// Same-site relative path only; forwarded to WorkOS state and consumed by
// /auth/callback (callback.vue). Mirrors GoogleButton's redirect -> returnTo mapping.
const returnTo = computed(() => resolveRedirect(props.redirect));

// A whitespace-only value left the button enabled (raw `email` is truthy) but
// continueSso trims it to "" and returns before showing anything — a dead
// click with no feedback. Gate on the same trimmed value continueSso acts on.
const trimmedEmail = computed(() => email.value.trim());

function handleEmailChange(field: { value: string }) {
  email.value = field.value;
}

async function continueSso() {
  // TextInput debounces returnValue by 500ms, so `email` lags what the user can
  // see — a fast Enter or a click right after a correction would look up the
  // previous domain, or nothing at all on a freshly-typed field.
  emailInput.value?.flush?.();

  const trimmed = trimmedEmail.value;
  if (!trimmed || loading.value) return;
  // Cheap client-side shape check so the commonest typo never round-trips; the
  // backend enforces the real rule (and its 400 is surfaced below).
  if (!trimmed.includes("@")) {
    error.value = "Enter your full work email address, e.g. you@company.com";
    track("sso_lookup_failed", { reason: "invalid_email" });
    return;
  }
  const domain = trimmed.split("@")[1];
  loading.value = true;
  error.value = "";
  try {
    const ssoAvailable = await authStore.lookupSsoConnection(trimmed);
    if (!ssoAvailable) {
      error.value =
        "No SSO is set up for that email. Sign in with your password or Google instead.";
      track("sso_lookup_failed", { reason: "no_connection", domain });
      return;
    }
    const params = new URLSearchParams({ email: trimmed });
    if (returnTo.value) params.set("returnTo", returnTo.value);
    // Top-level navigation is REQUIRED: /authorize answers with a 302 to the
    // WorkOS-hosted IdP login page, which the browser must load as a document — a
    // $fetch would chase it as a cross-origin request and never show it.
    window.location.href = `${config.public.apiBase}/api/public/auth/authorize?${params.toString()}`;
  } catch (err) {
    // Surface the backend's message (e.g. its 400 "A valid email is required")
    // instead of swallowing it — same helper login/googleAuth/register/callback use.
    if (getErrorStatus(err) === 503) {
      error.value = SSO_DISABLED_COPY;
      track("sso_lookup_failed", { reason: "sso_disabled", domain });
      return;
    }
    error.value = formatHttpError(err, "We couldn't start SSO sign-in. Please try again.");
    track("sso_lookup_failed", { reason: "lookup_error", domain });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="sso-button">
    <DynamicButton
      v-if="!expanded"
      class="login-form__button"
      color="white"
      width="100%"
      text="Sign in with SSO"
      icon="lock"
      @click-event="expanded = true"
    />
    <template v-else>
      <TextInput
        ref="emailInput"
        :value="email"
        title="Work email"
        type="email"
        :is-required="true"
        place-holder="you@company.com"
        autocomplete="email"
        :login="true"
        :index="0"
        @return-value="handleEmailChange"
        @enter-pressed="continueSso"
      />
      <BannerError :message="error" />
      <DynamicButton
        class="login-form__button"
        color="white"
        width="100%"
        text="Continue with SSO"
        :icon="loading ? 'spinner' : 'lock'"
        :spinning="loading"
        :disabled="!trimmedEmail || loading"
        @click-event="continueSso"
      />
    </template>
  </div>
</template>

<style lang="scss">
// Same 1rem rhythm as the rest of LoginForm. Flex gap, because the banner only
// renders on error and the field has no bottom margin of its own.
.sso-button {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .banner-error {
    margin-bottom: 0;
  }
}
</style>
